import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useSectionColors } from '../../../hooks/useSectionColors';

const ADMIN_KEY = 'ibx_admin_access';
const CLICK_THRESHOLD = 5;

function clampOpacity(value: number) {
  if (Number.isNaN(value)) return 0.2;
  return Math.min(1, Math.max(0, value));
}

function hexToRgb(hex: string) {
  const clean = hex.replace('#', '').trim();

  if (clean.length === 3) {
    const r = parseInt(clean[0] + clean[0], 16);
    const g = parseInt(clean[1] + clean[1], 16);
    const b = parseInt(clean[2] + clean[2], 16);
    return { r, g, b };
  }

  if (clean.length === 6) {
    const r = parseInt(clean.slice(0, 2), 16);
    const g = parseInt(clean.slice(2, 4), 16);
    const b = parseInt(clean.slice(4, 6), 16);
    return { r, g, b };
  }

  return null;
}

function hexToRgba(hex: string, opacity: number) {
  const rgb = hexToRgb(hex);
  if (!rgb) return `rgba(15, 23, 42, ${opacity})`;
  return `rgba(${rgb.r}, ${rgb.g}, ${rgb.b}, ${clampOpacity(opacity)})`;
}

export default function Footer() {
  const currentYear = new Date().getFullYear();
  const [adminVisible, setAdminVisible] = useState(false);
  const [clickCount, setClickCount] = useState(0);
  const [clickTimer, setClickTimer] = useState<ReturnType<typeof setTimeout> | null>(null);
  const [colors] = useSectionColors();

  useEffect(() => {
    const stored = localStorage.getItem(ADMIN_KEY);
    if (stored === 'true') setAdminVisible(true);
  }, []);

  const handleCopyrightClick = () => {
    const newCount = clickCount + 1;
    setClickCount(newCount);

    if (clickTimer) clearTimeout(clickTimer);

    if (newCount >= CLICK_THRESHOLD) {
      const next = !adminVisible;
      setAdminVisible(next);
      localStorage.setItem(ADMIN_KEY, String(next));
      setClickCount(0);
      return;
    }

    const timer = setTimeout(() => setClickCount(0), 3000);
    setClickTimer(timer);
  };

  const footerLinks = {
    institucional: [
      { name: 'Quem Somos', path: '/quem-somos' },
      { name: 'Estratégia', path: '/estrategia' },
      { name: 'Como Funciona', path: '/como-funciona' },
      { name: 'Casos de Sucesso', path: '/casos-de-sucesso' },
      { name: 'Depoimentos', path: '/depoimentos' },
      { name: 'FAQ', path: '/faq' },
      { name: 'Blog', path: '/blog' },
    ],
    servicos: [
      { name: 'Seguro Automóvel', path: '/servicos/automovel' },
      { name: 'Vida Empresarial', path: '/servicos/vida-empresarial' },
      { name: 'Gestão de Contratos', path: '/servicos/gestao-contratos' },
      { name: 'Recuperação de Sinistros', path: '/servicos/recuperacao-sinistros' },
      { name: 'Consultoria Técnica', path: '/servicos/consultoria-tecnica' },
      { name: 'Suporte e Acompanhamento', path: '/servicos/suporte-acompanhamento' },
    ],
    contato: [
      { name: 'Contato', path: '/contato' },
      { name: 'Solicitar Análise Gratuita', path: '/#contato' },
    ]
  };

  const handleAnchorClick = (e: React.MouseEvent<HTMLAnchorElement>, path: string) => {
    if (path.startsWith('/#')) {
      e.preventDefault();
      const hash = path.replace('/#', '');
      const element = document.getElementById(hash);
      if (element) {
        const offset = 100;
        const elementPosition = element.getBoundingClientRect().top;
        const offsetPosition = elementPosition + window.pageYOffset - offset;

        window.scrollTo({
          top: offsetPosition,
          behavior: 'smooth'
        });
      }
    }
  };

  const footerConfig = colors?.footer;

  const getFooterStyle = (): React.CSSProperties => {
    if (!footerConfig) {
      return {
        background: 'linear-gradient(135deg, #2f5d57 0%, #183e3c 100%)'
      };
    }

    if (footerConfig.isActive === false) {
      return {
        display: 'none'
      };
    }

    switch (footerConfig.mode) {
      case 'solid':
        return {
          backgroundColor: footerConfig.solidColor || '#183e3c'
        };

      case 'overlay':
        return {
          background: hexToRgba(
            footerConfig.overlayColor || '#183e3c',
            footerConfig.overlayOpacity ?? 0.2
          )
        };

      case 'gradient':
        return {
          background: `linear-gradient(${
            footerConfig.gradientDirection || '135deg'
          }, ${
            hexToRgba(
              footerConfig.gradientFrom || '#2f5d57',
              footerConfig.gradientOpacity ?? 1
            )
          }, ${
            hexToRgba(
              footerConfig.gradientTo || '#183e3c',
              footerConfig.gradientOpacity ?? 1
            )
          })`
        };

      default:
        return {
          background: 'linear-gradient(135deg, #2f5d57 0%, #183e3c 100%)'
        };
    }
  };

  return (
    <footer style={getFooterStyle()} className="text-white" role="contentinfo">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12 sm:py-16 lg:py-20">

        {/* Main Footer Content */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-8 lg:gap-12 mb-10 sm:mb-12">

          {/* Brand Column */}
          <div className="sm:col-span-2 lg:col-span-1">
            <Link to="/" className="flex items-center gap-3 mb-4 sm:mb-6 cursor-pointer" aria-label="IBX Brasil - Página inicial">
              <div className="w-12 h-12 bg-white rounded-lg flex items-center justify-center shadow-lg flex-shrink-0">
                <i className="ri-shield-check-line text-2xl text-strategic-green-600" aria-hidden="true"></i>
              </div>
              <div className="flex flex-col">
                <span className="text-xl font-bold" style={{ fontFamily: "'Playfair Display', serif" }}>
                  IBX Brasil
                </span>
                <span className="text-xs text-gray-300">
                  Gestão de Riscos
                </span>
              </div>
            </Link>

            <p className="text-sm text-gray-200 leading-relaxed mb-6">
              Especialistas em gestão profissional de riscos e contratação estratégica de seguros há mais de 25 anos.
            </p>

            <address className="space-y-3 not-italic">
              <a
                href="https://wa.me/5577988080505"
                target="_blank"
                rel="nofollow noreferrer"
                className="flex items-center gap-3 text-sm text-gray-200 hover:text-white transition-colors cursor-pointer"
                aria-label="Falar com a IBX Brasil pelo WhatsApp: (77) 98808-0505"
              >
                <i className="ri-whatsapp-line text-lg flex-shrink-0" aria-hidden="true"></i>
                <span>(77) 98808-0505</span>
              </a>
              <a
                href="mailto:contato@ibxbrasil.com.br"
                className="flex items-center gap-3 text-sm text-gray-200 hover:text-white transition-colors cursor-pointer break-all"
                aria-label="Enviar e-mail para contato@ibxbrasil.com.br"
              >
                <i className="ri-mail-line text-lg flex-shrink-0" aria-hidden="true"></i>
                <span>contato@ibxbrasil.com.br</span>
              </a>
              <div className="flex items-start gap-3 text-sm text-gray-200">
                <i className="ri-map-pin-line text-lg mt-0.5 flex-shrink-0" aria-hidden="true"></i>
                <span>Vitória da Conquista, Bahia, Brasil</span>
              </div>
            </address>
          </div>

          {/* Links Columns */}
          <nav className="lg:col-span-1" aria-labelledby="footer-institucional">
            <h3 id="footer-institucional" className="text-base sm:text-lg font-bold mb-4 sm:mb-6">Institucional</h3>
            <ul className="space-y-2.5">
              {footerLinks.institucional.map((link) => (
                <li key={link.path}>
                  <Link
                    to={link.path}
                    className="text-sm text-gray-300 hover:text-white transition-colors cursor-pointer inline-block"
                  >
                    {link.name}
                  </Link>
                </li>
              ))}
            </ul>
          </nav>

          <nav className="lg:col-span-1" aria-labelledby="footer-servicos">
            <h3 id="footer-servicos" className="text-base sm:text-lg font-bold mb-4 sm:mb-6">Serviços</h3>
            <ul className="space-y-2.5">
              {footerLinks.servicos.map((link) => (
                <li key={link.path}>
                  <Link
                    to={link.path}
                    className="text-sm text-gray-300 hover:text-white transition-colors cursor-pointer inline-block"
                  >
                    {link.name}
                  </Link>
                </li>
              ))}
            </ul>
          </nav>

          <div className="lg:col-span-1">
            <nav aria-labelledby="footer-atendimento">
              <h3 id="footer-atendimento" className="text-base sm:text-lg font-bold mb-4 sm:mb-6">Atendimento</h3>
              <ul className="space-y-2.5">
                {footerLinks.contato.map((link) => (
                  <li key={link.path}>
                    <Link
                      to={link.path}
                      onClick={(e) => handleAnchorClick(e, link.path)}
                      className="text-sm text-gray-300 hover:text-white transition-colors cursor-pointer inline-block"
                    >
                      {link.name}
                    </Link>
                  </li>
                ))}
              </ul>
            </nav>

            <div className="mt-6 sm:mt-8">
              <h4 className="text-sm font-bold mb-3 sm:mb-4">Redes Sociais</h4>
              <div className="flex gap-3" role="list" aria-label="Redes sociais da IBX Brasil">
                <a
                  href="https://www.linkedin.com/company/ibx-brasil"
                  target="_blank"
                  rel="nofollow noreferrer"
                  className="w-10 h-10 bg-white/10 hover:bg-white/20 rounded-lg flex items-center justify-center transition-all hover:scale-110 cursor-pointer"
                  aria-label="Seguir IBX Brasil no LinkedIn"
                  role="listitem"
                >
                  <i className="ri-linkedin-fill text-xl" aria-hidden="true"></i>
                </a>
                <a
                  href="https://www.instagram.com/ibxbrasil"
                  target="_blank"
                  rel="nofollow noreferrer"
                  className="w-10 h-10 bg-white/10 hover:bg-white/20 rounded-lg flex items-center justify-center transition-all hover:scale-110 cursor-pointer"
                  aria-label="Seguir IBX Brasil no Instagram"
                  role="listitem"
                >
                  <i className="ri-instagram-line text-xl" aria-hidden="true"></i>
                </a>
                <a
                  href="https://www.facebook.com/ibxbrasil"
                  target="_blank"
                  rel="nofollow noreferrer"
                  className="w-10 h-10 bg-white/10 hover:bg-white/20 rounded-lg flex items-center justify-center transition-all hover:scale-110 cursor-pointer"
                  aria-label="Curtir IBX Brasil no Facebook"
                  role="listitem"
                >
                  <i className="ri-facebook-fill text-xl" aria-hidden="true"></i>
                </a>
              </div>
            </div>
          </div>

        </div>

        {/* Bottom Bar */}
        <div className="border-t border-white/20 pt-6 sm:pt-8 mt-10 sm:mt-12">
          <div className="flex flex-col sm:flex-row items-center justify-between gap-4 text-xs sm:text-sm text-gray-200">
            <p
              className="text-center sm:text-left select-none cursor-default"
              onClick={handleCopyrightClick}
              title=""
            >
              © {currentYear} IBX Brasil. Todos os direitos reservados.
            </p>
            <div className="flex flex-wrap items-center justify-center gap-4 sm:gap-6">
              <Link to="/politica-de-privacidade" className="hover:text-white transition-colors cursor-pointer whitespace-nowrap">
                Política de Privacidade
              </Link>
              {adminVisible && (
                <Link
                  to="/admin"
                  className="flex items-center gap-1 text-white/30 hover:text-white/70 transition-colors cursor-pointer text-xs whitespace-nowrap"
                  title="Painel Admin"
                  aria-label="Acessar painel administrativo"
                >
                  <i className="ri-settings-3-line text-xs" aria-hidden="true"></i>
                  <span>Admin</span>
                </Link>
              )}
            </div>
          </div>
        </div>

      </div>
    </footer>
  );
}